import React, { useEffect, useState } from 'react';
import { supabase } from '../../../supabaseClient';
import speakersPageDataRaw from '../../../data/speakers-page.json';
import { sanitizeText, isValidURL, validateLength } from '../../../utils/sanitize';

export default function PageContentTab() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageId, setPageId] = useState(null);
    
    // Form state corresponding to speakers-page.json structure
    const [formData, setFormData] = useState({
        hero: { tag: '', title: '', titleAccent: '', subtitle: '' },
        stats: [], // We won't make stats editable for now to keep it simple, or we can just ignore them
        cfpCta: { title: '', description: '', buttonText: '', buttonLink: '' }
    });

    useEffect(() => {
        fetchPageContent();
    }, []);

    const fetchPageContent = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('speakers_page_content')
            .select('*')
            .eq('page_name', 'speakers-page')
            .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned" which is fine for initial
            setError(error.message);
        } else if (data) {
            setPageId(data.id);
            setFormData(data.data);
        }
        setLoading(false);
    };

    const handleImportJson = async () => {
        if (!window.confirm("This will import page content from speakers-page.json. Are you sure?")) return;
        setLoading(true);
        
        try {
            const { error } = await supabase.from('speakers_page_content').insert([{
                page_name: 'speakers-page',
                data: speakersPageDataRaw
            }]);
            
            if (error) throw error;
            
            alert('Import successful!');
            fetchPageContent();
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // ── Validate ──
        if (formData.hero) {
            const titleCheck = validateLength(formData.hero.title || '', 200, 0);
            if (!titleCheck.valid) { setError(`Hero title: ${titleCheck.reason}`); setLoading(false); return; }
            const subtitleCheck = validateLength(formData.hero.subtitle || '', 500, 0);
            if (!subtitleCheck.valid) { setError(`Subtitle: ${subtitleCheck.reason}`); setLoading(false); return; }
        }
        if (formData.cfpCta?.buttonLink && !isValidURL(formData.cfpCta.buttonLink)) {
            setError('Button link must be a valid https:// URL or a relative path.'); setLoading(false); return;
        }

        // ── Sanitize ──
        const cleanData = {
            hero: {
                tag: sanitizeText(formData.hero?.tag || '').slice(0, 100),
                title: sanitizeText(formData.hero?.title || '').slice(0, 200),
                titleAccent: sanitizeText(formData.hero?.titleAccent || '').slice(0, 100),
                subtitle: sanitizeText(formData.hero?.subtitle || '').slice(0, 500),
            },
            stats: formData.stats || [],
            cfpCta: {
                title: sanitizeText(formData.cfpCta?.title || '').slice(0, 200),
                description: sanitizeText(formData.cfpCta?.description || '').slice(0, 500),
                buttonText: sanitizeText(formData.cfpCta?.buttonText || '').slice(0, 50),
                buttonLink: (formData.cfpCta?.buttonLink || '').trim().slice(0, 2048),
            }
        };
        
        if (pageId) {
            // Update
            const { error: updateError } = await supabase
                .from('speakers_page_content')
                .update({ data: cleanData })
                .eq('id', pageId);
            if (updateError) setError(updateError.message);
        } else {
            // Insert
            const { data, error: insertError } = await supabase
                .from('speakers_page_content')
                .insert([{ page_name: 'speakers-page', data: cleanData }])
                .select();
            if (insertError) {
                setError(insertError.message);
            } else if (data && data.length > 0) {
                setPageId(data[0].id);
            }
        }
        
        if (!error) {
            alert('Page content saved successfully!');
        }
        setLoading(false);
    };

    if (!pageId && !loading && !error) {
        return (
            <div className="card shadow-lg border-0 rounded-4 text-center py-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h4 className="text-white mb-3">No Page Content Found</h4>
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>Please import the initial content from JSON.</p>
                <div>
                    <button onClick={handleImportJson} className="btn btn-outline-warning">
                        <i className="fa fa-download me-2"></i>Import from JSON
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {error && <div className="alert alert-danger">{error}</div>}
            
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4 d-flex justify-content-between align-items-center">
                    <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>Speakers Page Content</h4>
                </div>
                
                <div className="card-body px-4 pb-4 text-white">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <h5 className="mb-3 mt-2" style={{ color: 'var(--eventflow-primary)' }}>Hero Section</h5>
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Tag / Eyebrow</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                        value={formData.hero?.tag || ''} 
                                        onChange={e => setFormData({...formData, hero: {...formData.hero, tag: e.target.value}})} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title (White text)</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                        value={formData.hero?.title || ''} 
                                        onChange={e => setFormData({...formData, hero: {...formData.hero, title: e.target.value}})} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title Accent (Colored text)</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                        value={formData.hero?.titleAccent || ''} 
                                        onChange={e => setFormData({...formData, hero: {...formData.hero, titleAccent: e.target.value}})} 
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Subtitle / Description</label>
                                    <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" 
                                        value={formData.hero?.subtitle || ''} 
                                        onChange={e => setFormData({...formData, hero: {...formData.hero, subtitle: e.target.value}})} 
                                    ></textarea>
                                </div>
                            </div>
                            
                            <hr style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                            
                            <h5 className="mb-3 mt-4" style={{ color: 'var(--eventflow-primary)' }}>CFP Call To Action Box</h5>
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Title</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                        value={formData.cfpCta?.title || ''} 
                                        onChange={e => setFormData({...formData, cfpCta: {...formData.cfpCta, title: e.target.value}})} 
                                    />
                                </div>
                                <div className="col-12">
                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Description</label>
                                    <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="2" 
                                        value={formData.cfpCta?.description || ''} 
                                        onChange={e => setFormData({...formData, cfpCta: {...formData.cfpCta, description: e.target.value}})} 
                                    ></textarea>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Button Text</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                        value={formData.cfpCta?.buttonText || ''} 
                                        onChange={e => setFormData({...formData, cfpCta: {...formData.cfpCta, buttonText: e.target.value}})} 
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Button Link</label>
                                    <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} 
                                        value={formData.cfpCta?.buttonLink || ''} 
                                        onChange={e => setFormData({...formData, cfpCta: {...formData.cfpCta, buttonLink: e.target.value}})} 
                                    />
                                </div>
                            </div>
                            
                            <div className="mt-5">
                                <button type="submit" className="btn text-white fw-bold px-5" style={{ background: 'var(--eventflow-primary)' }} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Page Content'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
}
